// ─────────────────────────────────────────────────────────────────────────────
// shape_detector.ts — Unified single-stroke geometry classifier
//
// Pure functions. Take an array of stroke points, return a `StrokeShape`
// describing what the stroke looks like. The detector knows nothing about
// spells, selection, or page state — it just answers "what shape did the
// user draw?".
//
// Classifier order (winner-take-all on first confident match):
//   1. dot      — tiny extent, brief duration
//   2. closed   — endpoint loops back near the start (lasso)
//   3. circle   — closed AND fits a low-residual circle
//   4. line     — straightness above threshold, short arc-to-chord ratio
//   5. curve    — fallback for "drawn, not classified" strokes
//   6. unknown  — degenerate cases (insufficient points)
//
// Spiral detection lives in detection/spiral.ts and is invoked here only
// when the shape is closed-ish but with low circle-fit confidence.
// ─────────────────────────────────────────────────────────────────────────────

import type { Point, RegionBounds } from '@shared/notebook_model';
import { detectSpiral } from './spiral';

// ── Public types ────────────────────────────────────────────────────────────

export type StrokeShape =
  | { kind: 'dot'; center: Point }
  | { kind: 'line'; from: Point; to: Point; length: number; speedPxPerMs: number; straightness: number }
  | { kind: 'curve'; chord: number; arcLen: number; bend: number }
  | { kind: 'closed'; bounds: RegionBounds; convexity: number; closure: number }
  | { kind: 'circle'; center: Point; r: number; closure: number; fitError: number }
  | { kind: 'spiral'; center: Point; turns: number; sense: 'cw' | 'ccw'; radiusGrowth: number }
  | { kind: 'unknown'; pointCount: number };

export interface DetectInputPoint { x: number; y: number; timestamp: number }
export type DetectInput = ReadonlyArray<DetectInputPoint>;

// ── Tunables (px / ms) ──────────────────────────────────────────────────────

const DOT_MAX_EXTENT_PX   = 6;
const DOT_MAX_DURATION_MS = 320;
const MIN_POINTS_FOR_LINE = 4;
const LINE_STRAIGHTNESS   = 0.85;   // chord / arcLength — forgiving for
                                    // hand-drawn slashes (slight wobble OK)
// Lasso closure is forgiving on purpose: people don't draw clean loops.
// A stroke counts as "closed" if EITHER:
//   - endpoint gap closure (small absolute gap OR small gap relative to arc) AND
//     enough arc length, OR
//   - winding angle around centroid covers most of a full turn (i.e. the
//     stroke clearly intends to enclose something even if endpoints don't meet)
const CLOSURE_RATIO       = 0.25;
const CLOSURE_ABS_PX      = 40;
const MIN_LASSO_ARC_PX    = 50;
// Only near-full-loop strokes register as closed via winding; anything
// less (a C-shape, a kink) falls through to line/curve. A straight
// line's winding around its own centroid is exactly π, which is why
// the threshold needs a healthy margin above π.
const WINDING_MIN_RAD     = 1.4 * Math.PI;   // ~252°
const CIRCLE_FIT_TOL      = 0.10;   // mean radial deviation / mean radius
                                    // (kept tight so wonky lassos land in 'closed')

// ── Helpers ─────────────────────────────────────────────────────────────────

function bbox(pts: DetectInput): { x: number; y: number; w: number; h: number } {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

function arcLength(pts: DetectInput): number {
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const dx = pts[i].x - pts[i - 1].x;
    const dy = pts[i].y - pts[i - 1].y;
    total += Math.sqrt(dx * dx + dy * dy);
  }
  return total;
}

function chord(pts: DetectInput): number {
  if (pts.length < 2) return 0;
  const a = pts[0], b = pts[pts.length - 1];
  const dx = b.x - a.x, dy = b.y - a.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function centroid(pts: DetectInput): Point {
  let sx = 0, sy = 0;
  for (const p of pts) { sx += p.x; sy += p.y; }
  return { x: sx / pts.length, y: sy / pts.length };
}

function meanRadius(pts: DetectInput, c: Point): number {
  let s = 0;
  for (const p of pts) {
    const dx = p.x - c.x, dy = p.y - c.y;
    s += Math.sqrt(dx * dx + dy * dy);
  }
  return s / pts.length;
}

/** Total signed heading change around the centroid (radians, unwrapped).
 *  |result| ≥ 2π indicates a full revolution. Used to recognise lassos
 *  whose endpoints don't quite meet but which still enclose meaningful
 *  area. */
function totalWinding(pts: DetectInput, c: Point): number {
  if (pts.length < 3) return 0;
  let prev = Math.atan2(pts[0].y - c.y, pts[0].x - c.x);
  let sum = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = Math.atan2(pts[i].y - c.y, pts[i].x - c.x);
    let d = a - prev;
    while (d >  Math.PI) d -= 2 * Math.PI;
    while (d < -Math.PI) d += 2 * Math.PI;
    sum += d;
    prev = a;
  }
  return sum;
}

/** Signed area / convex-hull-area ratio — proxy for convexity.
 *  Returns 1 for convex, < 1 for concave / spiral / self-intersecting. */
function convexity(pts: DetectInput): number {
  if (pts.length < 3) return 1;
  let s = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i], b = pts[(i + 1) % pts.length];
    s += a.x * b.y - b.x * a.y;
  }
  const polyArea = Math.abs(s) / 2;
  // bbox area as a (cheap, loose) upper bound for convex-hull area
  const bb = bbox(pts);
  const bbArea = Math.max(bb.w * bb.h, 1);
  return Math.min(1, polyArea / bbArea);
}

function buildRegion(pts: DetectInput): RegionBounds {
  const bb = bbox(pts);
  return {
    points: pts.map(p => ({ x: p.x, y: p.y })),
    bbox: { x: bb.x, y: bb.y, width: bb.w, height: bb.h },
  };
}

// ── Public API ──────────────────────────────────────────────────────────────

/** Classify a stroke into a single shape. The caller owns input — points
 *  are read but not mutated. */
export function detectShape(pts: DetectInput): StrokeShape {
  if (pts.length < 2) return { kind: 'unknown', pointCount: pts.length };

  const bb = bbox(pts);
  const extent = Math.max(bb.w, bb.h);
  const dur    = pts[pts.length - 1].timestamp - pts[0].timestamp;

  // 1. Dot — very tight, very brief.
  if (extent <= DOT_MAX_EXTENT_PX && dur <= DOT_MAX_DURATION_MS) {
    return { kind: 'dot', center: centroid(pts) };
  }

  const arc = arcLength(pts);
  if (arc < 1) return { kind: 'unknown', pointCount: pts.length };
  const ch  = chord(pts);
  const straightness = ch / arc;

  // 2. Line — high straightness wins outright.  Checked BEFORE the
  // closed classifier so a straight stroke whose winding around its
  // centroid happens to be near π (a known mathematical artifact for
  // straight lines passing through their own midpoint) doesn't get
  // mis-classified as a lasso.
  if (pts.length >= MIN_POINTS_FOR_LINE && straightness >= LINE_STRAIGHTNESS) {
    const a = pts[0], b = pts[pts.length - 1];
    return {
      kind: 'line',
      from: { x: a.x, y: a.y },
      to:   { x: b.x, y: b.y },
      length: ch,
      speedPxPerMs: dur > 0 ? ch / dur : 0,
      straightness,
    };
  }

  // 3. Closed — either explicit endpoint closure OR strong winding.
  // Straight strokes are already routed to `line` above, so the
  // winding fallback only sees curved geometry.
  const closureGap   = ch;
  const closureRatio = closureGap / arc;
  const endpointClosed = (closureRatio <= CLOSURE_RATIO || closureGap <= CLOSURE_ABS_PX)
              && arc >= MIN_LASSO_ARC_PX
              && pts.length >= 8;

  let windingClosed = false;
  if (!endpointClosed && pts.length >= 8 && arc >= MIN_LASSO_ARC_PX) {
    const cent = centroid(pts);
    const winding = Math.abs(totalWinding(pts, cent));
    if (winding >= WINDING_MIN_RAD) windingClosed = true;
  }

  if (endpointClosed || windingClosed) {
    const spiral = detectSpiral(pts);
    if (spiral) return spiral;

    const c = centroid(pts);
    const r = meanRadius(pts, c);
    let dev = 0;
    for (const p of pts) {
      const dx = p.x - c.x, dy = p.y - c.y;
      dev += Math.abs(Math.sqrt(dx * dx + dy * dy) - r);
    }
    dev /= pts.length;
    const fitError = dev / Math.max(r, 1);
    if (fitError < CIRCLE_FIT_TOL) {
      return {
        kind: 'circle',
        center: c,
        r,
        closure: 1 - Math.min(1, closureRatio / CLOSURE_RATIO),
        fitError,
      };
    }
    return {
      kind: 'closed',
      bounds: buildRegion(pts),
      convexity: convexity(pts),
      closure: 1 - Math.min(1, closureRatio / CLOSURE_RATIO),
    };
  }

  // 4. Curve — fallback "drawn something".
  return {
    kind: 'curve',
    chord: ch,
    arcLen: arc,
    bend: Math.max(0, 1 - ch / Math.max(arc, 1)),
  };
}
